import { Fragment, useEffect, useRef } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { ClipboardIcon } from '@heroicons/react/24/outline'
import { TranscriptFormatterView } from './TranscriptFormatterView'
import { useFormattedExportStore } from './useFormattedExportStore'
import toast from 'react-hot-toast'
import { Transcript } from 'interfaces/db/transcript'

export const FormattedExportModal = ({
  submission_id,
  transcript,
  open,
  setOpen,
}: {
  submission_id?: string
  transcript?: Transcript
  open: boolean
  setOpen: (boolean) => void
}) => {
  const cancelButtonRef = useRef(null)
  const { setTranscript, exportTranscript } = useFormattedExportStore()

  useEffect(() => {
    // set the transcript in the store
    if (transcript) setTranscript(transcript)
  }, [transcript])

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          initialFocus={cancelButtonRef}
          onClose={setOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative flex transform flex-col justify-end overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6">
                  <div className="flex items-center justify-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                      <ClipboardIcon
                        className="h-6 w-6 text-green-600"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="text-center">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-semibold leading-6 text-slate-900"
                      >
                        Copy Transcript
                      </Dialog.Title>
                    </div>
                  </div>
                  <div className="mt-5 border-y border-slate-200">
                    <TranscriptFormatterView />
                  </div>
                  <div className="mt-5 flex justify-end gap-3 sm:mt-6">
                    <button
                      className="inline-flex justify-center rounded-md border border-transparent bg-toasty-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-toasty-blue-700 focus:outline-none focus:ring-2 focus:ring-toasty-blue-500 focus:ring-offset-2"
                      onClick={() => {
                        const t = exportTranscript()
                        navigator.clipboard.writeText(t || '')
                        toast.success(
                          'Formatted transcript saved to clipboard.'
                        )
                        setOpen(false)
                      }}
                    >
                      Copy to Clipboard{' '}
                      <ClipboardIcon
                        className="ml-2 -mr-1 h-5 w-5"
                        aria-hidden="true"
                      />
                    </button>
                    <button
                      className="inline-flex justify-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-toasty-blue-500 focus:ring-offset-2"
                      onClick={() => setOpen(false)}
                      ref={cancelButtonRef}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  )
}
